/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: menuDefinition.ts
 🗓️ Created: 2026-02-15
 🧩 Description:
    Hardcoded menu structure for the platform sidebar.
    Replaces database-driven menu loading for instant rendering.
    Uses i18n keys for translations and slug-based identifiers
    for permission matching.

 🔧 Notes:
    - mainTenantOnly: Items only visible to the main tenant
    - slug: Unique identifier matching MODULE_SLUGS in authorization.ts
    - titleKey: i18n key for translation (menu.sections.<slug>)
    - Level 3 items are the actual modules with permissions

 🔁 Last Updated: 2026-02-15 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

export interface MenuItemDef {
    slug: string;
    titleKey: string;
    icon: string;
    url: string;
    level: 1 | 2 | 3;
    mainTenantOnly?: boolean;
    appId?: 'portal' | 'switchboard' | string; // 🆕 Tied to a dynamic SSO app
    external?: boolean; // 🆕 Opens in a new tab
    children?: MenuItemDef[];
}

/**
 * Complete menu definition for the Ring2All Platform.
 * Structure: Level 1 (Sections) → Level 2 (Groups) → Level 3 (Modules)
 */
export const MENU_DEFINITION: MenuItemDef[] = [
    // =========================================================================
    // PBX
    // =========================================================================
    {
        slug: 'pbx', titleKey: 'menu.sections.pbx', icon: 'phone', url: '/pbx', level: 1,
        children: [
            // Extensions
            {
                slug: 'pbx_extensions', titleKey: 'menu.sections.pbx_extensions', icon: 'phone', url: '/pbx/extensions', level: 2,
                children: [
                    { slug: 'pbx_extensions_extensions', titleKey: 'menu.sections.pbx_extensions_extensions', icon: 'phone', url: '/pbx/extensions/extensions', level: 3 },
                    { slug: 'pbx_extensions_hot_desking', titleKey: 'menu.sections.pbx_extensions_hot_desking', icon: 'users', url: '/pbx/extensions/hot-desking', level: 3 },
                    { slug: 'pbx_extensions_bulk_modification', titleKey: 'menu.sections.pbx_extensions_bulk_modification', icon: 'edit-3', url: '/pbx/extensions/bulk-modification', level: 3 },
                    { slug: 'pbx_extensions_status', titleKey: 'menu.sections.pbx_extensions_status', icon: 'activity', url: '/pbx/extensions/status', level: 3 },
                ]
            },
            // Applications
            {
                slug: 'pbx_applications', titleKey: 'menu.sections.pbx_applications', icon: 'grid-3x3', url: '/pbx/applications', level: 2,
                children: [
                    { slug: 'pbx_applications_conferences', titleKey: 'menu.sections.pbx_applications_conferences', icon: 'video', url: '/pbx/applications/conferences', level: 3 },
                    { slug: 'pbx_applications_direct_dial', titleKey: 'menu.sections.pbx_applications_direct_dial', icon: 'phone', url: '/pbx/applications/direct-dial', level: 3 },
                    { slug: 'pbx_applications_direct_route', titleKey: 'menu.sections.pbx_applications_direct_route', icon: 'map-pin', url: '/pbx/applications/direct-route', level: 3 },
                    { slug: 'pbx_applications_paging', titleKey: 'menu.sections.pbx_applications_paging', icon: 'megaphone', url: '/pbx/applications/paging', level: 3 },
                    { slug: 'pbx_applications_custom_applications', titleKey: 'menu.sections.pbx_applications_custom_applications', icon: 'folder', url: '/pbx/applications/custom-applications', level: 3 },
                    { slug: 'pbx_applications_feature_codes', titleKey: 'menu.sections.pbx_applications_feature_codes', icon: 'hash', url: '/pbx/applications/feature-codes', level: 3 },
                    { slug: 'pbx_applications_pickup_groups', titleKey: 'menu.sections.pbx_applications_pickup_groups', icon: 'hand', url: '/pbx/applications/pickup-groups', level: 3 },
                    { slug: 'pbx_applications_parking', titleKey: 'menu.sections.pbx_applications_parking', icon: 'car', url: '/pbx/applications/parking', level: 3 },
                    { slug: 'pbx_applications_speed_dialing', titleKey: 'menu.sections.pbx_applications_speed_dials', icon: 'zap', url: '/pbx/applications/speed-dialing', level: 3 },
                    { slug: 'pbx_applications_voicemail_broadcast', titleKey: 'menu.sections.pbx_applications_voicemail_broadcast', icon: 'volume-2', url: '/pbx/applications/voicemail-broadcast', level: 3 },
                    { slug: 'pbx_applications_callback', titleKey: 'menu.sections.pbx_applications_callback', icon: 'phone-call', url: '/pbx/applications/callback', level: 3 },
                    { slug: 'pbx_applications_disa', titleKey: 'menu.sections.pbx_applications_disa', icon: 'external-link', url: '/pbx/applications/disa', level: 3 },
                    { slug: 'pbx_applications_dynamic_destination', titleKey: 'menu.sections.pbx_applications_dynamic_destination', icon: 'navigation', url: '/pbx/applications/dynamic-destination', level: 3 },
                    { slug: 'pbx_applications_phonebooks', titleKey: 'menu.sections.pbx_applications_phonebooks', icon: 'book', url: '/pbx/applications/phonebooks', level: 3 },
                ]
            },
            // Class of Service
            {
                slug: 'pbx_class_of_service', titleKey: 'menu.sections.pbx_class_of_service', icon: 'shield', url: '/pbx/class-of-service', level: 2,
                children: [
                    { slug: 'pbx_cos_class', titleKey: 'menu.sections.pbx_cos_class', icon: 'shield', url: '/pbx/class-of-service/class', level: 3 },
                    { slug: 'pbx_cos_feature_categories', titleKey: 'menu.sections.pbx_cos_feature_categories', icon: 'layers', url: '/pbx/class-of-services/feature-categories', level: 3 },
                    { slug: 'pbx_cos_dial_restriction', titleKey: 'menu.sections.pbx_cos_dial_restriction', icon: 'ban', url: '/pbx/class-of-service/dial-restriction', level: 3 },
                    { slug: 'pbx_cos_customer_codes', titleKey: 'menu.sections.pbx_cos_customer_codes', icon: 'credit-card', url: '/pbx/class-of-service/customer-codes', level: 3 },
                    { slug: 'pbx_cos_auth_codes', titleKey: 'menu.sections.pbx_cos_auth_codes', icon: 'key', url: '/pbx/class-of-service/auth-codes', level: 3 },
                    { slug: 'pbx_cos_route_selections', titleKey: 'menu.sections.pbx_cos_route_selections', icon: 'route', url: '/pbx/class-of-service/route-selections', level: 3 },
                ]
            },
            // Call Center
            {
                slug: 'pbx_call_center', titleKey: 'menu.sections.pbx_call_center', icon: 'headphones', url: '/pbx/call-center', level: 2,
                children: [
                    { slug: 'pbx_call_center_ring_groups', titleKey: 'menu.sections.pbx_call_center_ring_groups', icon: 'users', url: '/pbx/call-center/ring-groups', level: 3 },
                    { slug: 'pbx_call_center_agents', titleKey: 'menu.sections.pbx_call_center_agents', icon: 'user', url: '/pbx/call-center/agents', level: 3 },
                    { slug: 'pbx_call_center_queues', titleKey: 'menu.sections.pbx_call_center_queues', icon: 'list', url: '/pbx/call-center/queues', level: 3 },
                    { slug: 'pbx_call_center_priority_routing', titleKey: 'menu.sections.pbx_call_center_priority_routing', icon: 'arrow-up', url: '/pbx/call-center/priority-routing', level: 3 },
                    { slug: 'pbx_call_center_queue_vip', titleKey: 'menu.sections.pbx_call_center_queue_vip', icon: 'crown', url: '/pbx/call-center/vip-lists', level: 3 },
                    { slug: 'pbx_call_center_callback_profiles', titleKey: 'menu.sections.pbx_call_center_callback_profiles', icon: 'phone-call', url: '/pbx/call-center/callback-profiles', level: 3 },
                ]
            },
            // Calls Routing
            {
                slug: 'pbx_calls_routing', titleKey: 'menu.sections.pbx_calls_routing', icon: 'route', url: '/pbx/calls-routing', level: 2,
                children: [
                    { slug: 'pbx_routing_gateways', titleKey: 'menu.sections.pbx_routing_gateways', icon: 'cable', url: '/pbx/calls-routing/gateways', level: 3 },
                    { slug: 'pbx_routing_outbound', titleKey: 'menu.sections.pbx_routing_outbound', icon: 'arrow-right', url: '/pbx/calls-routing/outbound', level: 3 },
                    { slug: 'pbx_routing_inbound', titleKey: 'menu.sections.pbx_routing_inbound', icon: 'arrow-left', url: '/pbx/calls-routing/inbound', level: 3 },
                    { slug: 'pbx_routing_dynamic', titleKey: 'menu.sections.pbx_routing_dynamic', icon: 'refresh-cw', url: '/pbx/calls-routing/dynamic', level: 3 },
                    { slug: 'pbx_routing_pin_list', titleKey: 'menu.sections.pbx_routing_pin_list', icon: 'key', url: '/pbx/calls-routing/pin-list', level: 3 },
                ]
            },
            // Incoming Call Tools
            {
                slug: 'pbx_incoming_tools', titleKey: 'menu.sections.pbx_incoming_tools', icon: 'phone', url: '/pbx/incoming-tools', level: 2,
                children: [
                    { slug: 'pbx_tools_ivr', titleKey: 'menu.sections.pbx_tools_ivr', icon: 'menu', url: '/pbx/incoming-tools/ivr', level: 3 },
                    { slug: 'pbx_tools_time_groups', titleKey: 'menu.sections.pbx_tools_time_groups', icon: 'clock', url: '/pbx/incoming-tools/time-groups', level: 3 },
                    { slug: 'pbx_tools_time_conditions', titleKey: 'menu.sections.pbx_tools_time_conditions', icon: 'calendar', url: '/pbx/incoming-tools/time-conditions', level: 3 },
                    { slug: 'pbx_tools_announcements', titleKey: 'menu.sections.pbx_tools_announcements', icon: 'volume-2', url: '/pbx/incoming-tools/announcements', level: 3 },
                    { slug: 'pbx_tools_languages', titleKey: 'menu.sections.pbx_tools_languages', icon: 'globe', url: '/pbx/incoming-tools/languages', level: 3 },
                    { slug: 'pbx_tools_call_flows', titleKey: 'menu.sections.pbx_tools_call_flows', icon: 'git-branch', url: '/pbx/incoming-tools/call-flows', level: 3 },
                    { slug: 'pbx_tools_cid_modifiers', titleKey: 'menu.sections.pbx_tools_cid_modifiers', icon: 'edit', url: '/pbx/incoming-tools/cid-modifiers', level: 3 },
                    { slug: 'pbx_tools_cid_lookup', titleKey: 'menu.sections.pbx_tools_cid_lookup', icon: 'search', url: '/pbx/incoming-tools/callerid-lookup-sources', level: 3 },
                    { slug: 'pbx_tools_auth_codes', titleKey: 'menu.sections.pbx_tools_auth_codes', icon: 'key', url: '/pbx/incoming-tools/auth-codes', level: 3 },
                    { slug: 'pbx_incoming_tools_blacklist', titleKey: 'menu.sections.pbx_incoming_tools_blacklist', icon: 'ban', url: '/pbx/incoming-tools/blacklist', level: 3 },
                ]
            },
            // SMS Messaging
            {
                slug: 'pbx_sms', titleKey: 'menu.sections.pbx_sms', icon: 'message-square', url: '/pbx/sms', level: 2,
                children: [
                    { slug: 'pbx_sms_providers', titleKey: 'menu.sections.pbx_sms_providers', icon: 'server', url: '/pbx/sms/providers', level: 3 },
                    { slug: 'pbx_sms_numbers', titleKey: 'menu.sections.pbx_sms_numbers', icon: 'phone', url: '/pbx/sms/numbers', level: 3 },
                    { slug: 'pbx_sms_routes', titleKey: 'menu.sections.pbx_sms_routes', icon: 'route', url: '/pbx/sms/routes', level: 3 },
                    { slug: 'pbx_sms_messages', titleKey: 'menu.sections.pbx_sms_messages', icon: 'message-circle', url: '/pbx/sms/messages', level: 3 },
                    { slug: 'pbx_sms_settings', titleKey: 'menu.sections.pbx_sms_settings', icon: 'settings', url: '/pbx/sms/settings', level: 3 },
                    { slug: 'pbx_sms_optouts', titleKey: 'menu.sections.pbx_sms_optouts', icon: 'user-x', url: '/pbx/sms/optouts', level: 3 },
                ]
            },
            // Tools
            {
                slug: 'pbx_tools', titleKey: 'menu.sections.pbx_tools', icon: 'wrench', url: '/pbx/tools', level: 2,
                children: [
                    { slug: 'pbx_tools_cli', titleKey: 'menu.sections.pbx_tools_cli', icon: 'terminal', url: '/pbx/tools/cli', level: 3 },
                    { slug: 'pbx_tools_dashboard', titleKey: 'menu.sections.pbx_tools_dashboard', icon: 'bar-chart-3', url: '/pbx/tools/dashboard', level: 3 },
                    { slug: 'pbx_tools_log_viewer', titleKey: 'menu.sections.pbx_tools_log_viewer', icon: 'file-text', url: '/pbx/tools/log-viewer', level: 3 },
                    { slug: 'pbx_tools_weak_password', titleKey: 'menu.sections.pbx_tools_weak_password', icon: 'alert-triangle', url: '/pbx/tools/weak-password', level: 3 },
                ]
            },
            // Emergency Calls
            {
                slug: 'pbx_emergency', titleKey: 'menu.sections.pbx_emergency', icon: 'alert-circle', url: '/pbx/emergency', level: 2,
                children: [
                    { slug: 'pbx_emergency_numbers', titleKey: 'menu.sections.pbx_emergency_numbers', icon: 'phone', url: '/pbx/emergency/numbers', level: 3 },
                    { slug: 'pbx_emergency_locations', titleKey: 'menu.sections.pbx_emergency_locations', icon: 'map-pin', url: '/pbx/emergency/locations', level: 3 },
                ]
            },
            // Virtual Faxes
            {
                slug: 'pbx_virtual_faxes', titleKey: 'menu.sections.pbx_virtual_faxes', icon: 'file-text', url: '/pbx/virtual-faxes', level: 2,
                children: [
                    { slug: 'pbx_fax_devices', titleKey: 'menu.sections.pbx_fax_devices', icon: 'printer', url: '/pbx/virtual-faxes/devices', level: 3 },
                    { slug: 'pbx_fax_sending', titleKey: 'menu.sections.pbx_fax_sending', icon: 'send', url: '/pbx/virtual-faxes/sending', level: 3 },
                    { slug: 'pbx_fax_viewer', titleKey: 'menu.sections.pbx_fax_viewer', icon: 'eye', url: '/pbx/virtual-faxes/viewer', level: 3 },
                ]
            },
        ]
    },

    // =========================================================================
    // REPORTS
    // =========================================================================
    {
        slug: 'reports', titleKey: 'menu.sections.reports', icon: 'bar-chart-3', url: '/reports', level: 1,
        children: [
            // CDR Reports
            {
                slug: 'reports_cdr', titleKey: 'menu.sections.reports_cdr', icon: 'file-text', url: '/reports/cdr', level: 2,
                children: [
                    { slug: 'reports_cdr_filters', titleKey: 'menu.sections.reports_cdr_filters', icon: 'filter', url: '/reports/cdr/filters', level: 3 },
                    { slug: 'reports_cdr_cdr', titleKey: 'menu.sections.reports_cdr_cdr', icon: 'file-text', url: '/reports/cdr/cdr', level: 3 },
                ]
            },
            // PBX Reports
            {
                slug: 'reports_pbx', titleKey: 'menu.sections.reports_pbx', icon: 'phone', url: '/reports/pbx', level: 2,
                children: [
                    { slug: 'reports_pbx_active_calls', titleKey: 'menu.sections.reports_pbx_active_calls', icon: 'phone-call', url: '/reports/pbx/active-calls', level: 3 },
                    { slug: 'reports_pbx_sip_endpoints', titleKey: 'menu.sections.reports_pbx_sip_endpoints', icon: 'wifi', url: '/reports/pbx/sip-endpoints', level: 3 },
                ]
            },
            // IVR Reports
            {
                slug: 'reports_ivr', titleKey: 'menu.sections.reports_ivr', icon: 'menu', url: '/reports/ivr', level: 2,
                children: [
                    { slug: 'reports_ivr_stats', titleKey: 'menu.sections.reports_ivr_stats', icon: 'bar-chart-2', url: '/reports/ivr/stats', level: 3 },
                ]
            },
            // Call Center Reports
            {
                slug: 'reports_call_center', titleKey: 'menu.sections.reports_call_center', icon: 'headphones', url: '/reports/call-center', level: 2,
                children: [
                    { slug: 'reports_call_center_queue_callback', titleKey: 'menu.sections.reports_call_center_queue_callback', icon: 'phone-call', url: '/reports/call-center/queue-callback', level: 3 },
                ]
            },
            // System Reports
            {
                slug: 'reports_system', titleKey: 'menu.sections.reports_system', icon: 'server', url: '/reports/system', level: 2,
                children: [
                    { slug: 'reports_system_audit_logs', titleKey: 'menu.sections.reports_system_audit_logs', icon: 'file-text', url: '/reports/system/audit-logs', level: 3 },
                    { slug: 'reports_system_user_activity', titleKey: 'menu.sections.reports_system_user_activity', icon: 'user', url: '/reports/system/user-activity', level: 3 },
                    { slug: 'reports_system_change_history', titleKey: 'menu.sections.reports_system_change_history', icon: 'history', url: '/reports/system/change-history', level: 3 },
                    { slug: 'reports_system_security', titleKey: 'menu.sections.reports_system_security', icon: 'shield', url: '/reports/system/security', level: 3 },
                ]
            },
        ]
    },

    // =========================================================================
    // SETTINGS
    // =========================================================================
    {
        slug: 'settings', titleKey: 'menu.sections.settings', icon: 'settings', url: '/settings', level: 1,
        children: [
            // Technology Settings
            {
                slug: 'settings_technology', titleKey: 'menu.sections.settings_technology', icon: 'cpu', url: '/settings/technology', level: 2,
                children: [
                    { slug: 'settings_tech_sip', titleKey: 'menu.sections.settings_tech_sip', icon: 'wifi', url: '/settings/technology/sip', level: 3, mainTenantOnly: true },
                    { slug: 'settings_tech_device_profile', titleKey: 'menu.sections.settings_tech_device_profile', icon: 'smartphone', url: '/settings/technology/device-profile', level: 3 },
                    { slug: 'settings_tech_gateway_profile', titleKey: 'menu.sections.settings_tech_gateway_profile', icon: 'cable', url: '/settings/technology/gateway-profile', level: 3, mainTenantOnly: true },
                    { slug: 'settings_tech_dial_profile', titleKey: 'menu.sections.settings_tech_dial_profile', icon: 'phone', url: '/settings/technology/dial-profile', level: 3 },
                ]
            },
            // PBX Settings
            {
                slug: 'settings_pbx', titleKey: 'menu.sections.settings_pbx', icon: 'phone', url: '/settings/pbx', level: 2,
                children: [
                    { slug: 'settings_pbx_system_general', titleKey: 'menu.sections.settings_pbx_system_general', icon: 'settings', url: '/settings/pbx/system-general', level: 3 },
                    { slug: 'settings_pbx_system_vars', titleKey: 'menu.sections.settings_pbx_system_vars', icon: 'code', url: '/settings/pbx/system-vars', level: 3 },
                    { slug: 'settings_pbx_voicemail_settings', titleKey: 'menu.sections.settings_pbx_voicemail_settings', icon: 'settings', url: '/settings/pbx/voicemail-settings', level: 3 },
                    { slug: 'settings_pbx_conference_profiles', titleKey: 'menu.sections.settings_pbx_conference_profiles', icon: 'users', url: '/settings/pbx/conference-profiles', level: 3 },
                    { slug: 'settings_pbx_queue_profiles', titleKey: 'menu.sections.settings_pbx_queue_profiles', icon: 'phone', url: '/settings/pbx/queue-profiles', level: 3 },
                    { slug: 'settings_pbx_esl_users', titleKey: 'menu.sections.settings_pbx_esl_users', icon: 'user', url: '/settings/pbx/esl-users', level: 3 },
                    { slug: 'settings_pbx_log_settings', titleKey: 'menu.sections.settings_pbx_log_settings', icon: 'file-text', url: '/settings/pbx/log-files', level: 3 },
                    { slug: 'settings_pbx_cdr_settings', titleKey: 'menu.sections.settings_pbx_cdr_settings', icon: 'file-text', url: '/settings/pbx/cdr-settings', level: 3 },
                ]
            },
            // Voice Prompts
            {
                slug: 'settings_voice_prompts', titleKey: 'menu.sections.settings_voice_prompts', icon: 'volume-2', url: '/settings/voice-prompts', level: 2,
                children: [
                    { slug: 'settings_voice_pbx_guide', titleKey: 'menu.sections.settings_voice_pbx_guide', icon: 'volume-2', url: '/settings/voice-prompts/pbx-guide', level: 3 },
                    { slug: 'settings_voice_moh', titleKey: 'menu.sections.settings_voice_moh', icon: 'music', url: '/settings/voice-prompts/music-hold', level: 3 },
                    { slug: 'settings_voice_recording', titleKey: 'menu.sections.settings_voice_recording', icon: 'mic', url: '/settings/voice-prompts/recording', level: 3 },
                ]
            },
            // Provisioning
            {
                slug: 'settings_provisioning', titleKey: 'menu.sections.settings_provisioning', icon: 'smartphone', url: '/settings/provisioning', level: 2,
                children: [
                    { slug: 'settings_provisioning_vendors', titleKey: 'menu.sections.settings_provisioning_vendors', icon: 'factory', url: '/settings/provisioning/provisioning-vendors', level: 3 },
                    { slug: 'settings_provisioning_device_models', titleKey: 'menu.sections.settings_provisioning_device_models', icon: 'smartphone', url: '/settings/provisioning/provisioning-device-models', level: 3 },
                    { slug: 'settings_provisioning_server_settings', titleKey: 'menu.sections.settings_provisioning_server_settings', icon: 'server', url: '/settings/provisioning/provisioning-server-settings', level: 3 },
                    { slug: 'settings_provisioning_security_settings', titleKey: 'menu.sections.settings_provisioning_security_settings', icon: 'shield', url: '/settings/provisioning/provisioning-security-settings', level: 3 },
                    { slug: 'settings_provisioning_templates', titleKey: 'menu.sections.settings_provisioning_templates', icon: 'layout-template', url: '/settings/provisioning/provisioning-templates', level: 3 },
                    { slug: 'settings_provisioning_devices', titleKey: 'menu.sections.settings_provisioning_devices', icon: 'phone', url: '/settings/provisioning/provisioning-devices', level: 3 },
                ]
            },
            // Integrations
            {
                slug: 'settings_integrations', titleKey: 'menu.sections.settings_integrations', icon: 'link', url: '/settings/integrations', level: 2,
                children: [
                    { slug: 'settings_integrations_config', titleKey: 'menu.sections.settings_integrations_marketplace', icon: 'link', url: '/settings/integrations', level: 3 },
                ]
            },
        ]
    },

    // =========================================================================
    // ADMIN
    // =========================================================================
    {
        slug: 'admin', titleKey: 'menu.sections.admin', icon: 'shield', url: '/admin', level: 1,
        children: [
            // Administration
            {
                slug: 'admin_admin', titleKey: 'menu.sections.admin_admin', icon: 'settings', url: '/admin/admin', level: 2,
                children: [
                    { slug: 'admin_admin_users', titleKey: 'menu.sections.admin_admin_users', icon: 'users', url: '/users', level: 3 },
                    { slug: 'admin_admin_user_profiles', titleKey: 'menu.sections.admin_admin_user_profiles', icon: 'users', url: '/role-profiles', level: 3 },
                    { slug: 'admin_admin_log_profiles', titleKey: 'menu.sections.admin_admin_log_profiles', icon: 'file-text', url: '/log-profiles', level: 3 },
                    { slug: 'admin_admin_app_keys', titleKey: 'menu.sections.admin_admin_app_keys', icon: 'key', url: '/admin/admin/app-keys', level: 3 },
                    { slug: 'admin_admin_languages', titleKey: 'menu.sections.admin_admin_languages', icon: 'globe', url: '/settings/languages', level: 3 },
                ]
            },
            // System Settings
            {
                slug: 'admin_system_settings', titleKey: 'menu.sections.admin_system_settings', icon: 'server', url: '/admin/system-settings', level: 2,
                children: [
                    { slug: 'admin_telephony_servers', titleKey: 'menu.sections.admin_telephony_servers', icon: 'server', url: '/admin/system-settings/telephony-servers', level: 3, mainTenantOnly: true },
                    { slug: 'admin_system_misc', titleKey: 'menu.sections.admin_system_misc', icon: 'settings', url: '/admin/system-settings/misc', level: 3 },
                    { slug: 'admin_system_branding', titleKey: 'menu.sections.admin_system_branding', icon: 'palette', url: '/admin/system-settings/branding', level: 3 },
                    { slug: 'admin_system_email_settings', titleKey: 'menu.sections.admin_system_email_settings', icon: 'mail', url: '/admin/system-settings/email', level: 3 },
                    { slug: 'admin_system_email_template', titleKey: 'menu.sections.admin_system_email_template', icon: 'file-text', url: '/admin/system-settings/email-template', level: 3 },
                    { slug: 'admin_system_certificates', titleKey: 'menu.sections.admin_system_certificates', icon: 'certificate', url: '/admin/system-settings/certificates', level: 3, mainTenantOnly: true },
                    { slug: 'admin_system_http_server', titleKey: 'menu.sections.admin_system_http_server', icon: 'globe', url: '/admin/system-settings/http-server', level: 3, mainTenantOnly: true },
                ]
            },
            // Multi-Tenant
            {
                slug: 'admin_multi_tenant', titleKey: 'menu.sections.admin_multi_tenant', icon: 'building', url: '/admin/multi-tenant', level: 2,
                mainTenantOnly: true,
                children: [
                    { slug: 'admin_multi_tenant_tenants', titleKey: 'menu.sections.admin_multi_tenant_tenants', icon: 'building', url: '/admin/multi-tenant/tenants', level: 3, mainTenantOnly: true },
                    { slug: 'admin_multi_tenant_domains', titleKey: 'menu.sections.admin_multi_tenant_domains', icon: 'globe', url: '/admin/multi-tenant/domains', level: 3, mainTenantOnly: true },
                ]
            },
            // Firewall
            {
                slug: 'admin_firewall', titleKey: 'menu.sections.admin_firewall', icon: 'shield', url: '/admin/firewall', level: 2,
                mainTenantOnly: true,
                children: [
                    { slug: 'admin_firewall_settings', titleKey: 'menu.sections.admin_firewall_settings', icon: 'settings', url: '/admin/firewall/settings', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_access_control', titleKey: 'menu.sections.admin_firewall_access_control', icon: 'lock', url: '/admin/firewall/access-control', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_services', titleKey: 'menu.sections.admin_firewall_services', icon: 'server', url: '/admin/firewall/services', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_rules', titleKey: 'menu.sections.admin_firewall_rules', icon: 'list', url: '/admin/firewall/rules', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_geo', titleKey: 'menu.sections.admin_firewall_geo', icon: 'globe', url: '/admin/firewall/geo', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_voipbl', titleKey: 'menu.sections.admin_firewall_voipbl', icon: 'shield-alert', url: '/admin/firewall/voipbl', level: 3, mainTenantOnly: true },
                    { slug: 'admin_firewall_acl', titleKey: 'menu.sections.admin_firewall_acl', icon: 'shield', url: '/admin/firewall/acl', level: 3, mainTenantOnly: true },
                ]
            },
            // Network
            {
                slug: 'admin_network', titleKey: 'menu.sections.admin_network', icon: 'wifi', url: '/admin/network', level: 2,
                mainTenantOnly: true,
                children: [
                    { slug: 'admin_network_settings',        titleKey: 'menu.sections.admin_network_settings',        icon: 'settings', url: '/admin/network/settings',        level: 3, mainTenantOnly: true },
                    { slug: 'admin_network_openvpn',         titleKey: 'menu.sections.admin_network_openvpn',         icon: 'shield',   url: '/admin/network/openvpn',         level: 3, mainTenantOnly: true },
                    { slug: 'admin_network_openvpn_client',  titleKey: 'menu.sections.admin_network_openvpn_client',  icon: 'link',     url: '/admin/network/openvpn-client',  level: 3, mainTenantOnly: true },
                    { slug: 'admin_network_wireguard',       titleKey: 'menu.sections.admin_network_wireguard',       icon: 'wifi',     url: '/admin/network/wireguard',       level: 3, mainTenantOnly: true },
                ]
            },
            // Maintenance
            {
                slug: 'admin_maintenance', titleKey: 'menu.sections.admin_maintenance', icon: 'wrench', url: '/admin/maintenance', level: 2,
                children: [
                    { slug: 'admin_maintenance_backup', titleKey: 'menu.sections.admin_maintenance_backup', icon: 'download', url: '/admin/maintenance/backup', level: 3 },
                    { slug: 'admin_maintenance_cron_profiles', titleKey: 'menu.sections.admin_maintenance_cron_profiles', icon: 'clock', url: '/admin/maintenance/cron-profiles', level: 3 },
                    { slug: 'admin_maintenance_system', titleKey: 'menu.sections.admin_maintenance_system', icon: 'settings', url: '/admin/maintenance/system', level: 3, mainTenantOnly: true },
                    { slug: 'admin_maintenance_tasks', titleKey: 'menu.sections.admin_maintenance_tasks', icon: 'check-square', url: '/admin/maintenance/tasks', level: 3, mainTenantOnly: true },
                ]
            },
            // AI Integration
            {
                slug: 'admin_ai_integration', titleKey: 'menu.sections.admin_ai_integration', icon: 'bot', url: '/admin/ai-integration', level: 2,
                children: [
                    { slug: 'admin_ai_integration_providers', titleKey: 'menu.sections.admin_ai_integration_providers', icon: 'server', url: '/admin/ai-integration/providers', level: 3 },
                    { slug: 'admin_ai_integration_profiles', titleKey: 'menu.sections.admin_ai_integration_profiles', icon: 'settings', url: '/admin/ai-integration/profiles', level: 3 },
                    { slug: 'admin_ai_integration_tool_profiles', titleKey: 'menu.sections.admin_ai_integration_tool_profiles', icon: 'wrench', url: '/admin/ai-integration/tool-profiles', level: 3 },
                    { slug: 'admin_ai_integration_translator_profiles', titleKey: 'menu.sections.admin_ai_integration_translator_profiles', icon: 'languages', url: '/admin/ai-integration/translator-profiles', level: 3 },
                    { slug: 'admin_ai_integration_chatbots', titleKey: 'menu.sections.admin_ai_integration_chatbots', icon: 'message-square', url: '/admin/ai-integration/chatbots', level: 3 },
                    { slug: 'admin_ai_integration_agents', titleKey: 'menu.sections.admin_ai_integration_agents', icon: 'users', url: '/admin/ai-integration/agents', level: 3 },
                ]
            },
        ]
    },

    // =========================================================================
    // APPS (Dynamic Apps driven by SSO)
    // =========================================================================
    {
        slug: 'apps', titleKey: 'menu.sections.apps', icon: 'app-window', url: '/apps', level: 1,
        children: [
            {
                // Opens Portal via server-side SSO redirect (cookies carry the auth session)
                slug: 'apps_portal', titleKey: 'menu.sections.apps_portal', icon: 'user', url: '/api/apps/portal/redirect', level: 2, appId: 'portal', external: true
            },
            {
                // Opens Switchboard via server-side SSO redirect (cookies carry the auth session)
                slug: 'apps_switchboard', titleKey: 'menu.sections.apps_switchboard', icon: 'monitor', url: '/api/apps/switchboard/redirect', level: 2, appId: 'switchboard', external: true
            },
            {
                // Opens Billing via server-side SSO redirect (cookies carry the auth session)
                slug: 'apps_billing', titleKey: 'menu.sections.apps_billing', icon: 'credit-card', url: '/api/apps/billing/redirect', level: 2, appId: 'billing', external: true
            }
        ]
    },


];

/**
 * Utility: Build a filtered menu tree based on tenant and permissions.
 * 
 * @param isMainTenant - Whether the current user belongs to the main tenant
 * @param userPermissions - Array of { moduleId: string, level: string } from auth store.
 *                          moduleId here is actually the numeric DB ID as string.
 *                          For now we skip permission filtering until Phase 2 migrates to slugs.
 * @param permissionSlugs - Optional map of slug→level for direct slug-based permission checking
 * @param availableApps - Optional object specifying which dynamic SSO apps are available
 */
export function buildFilteredMenu(
    isMainTenant: boolean,
    permissionSlugs?: Map<string, string>,
    availableApps?: Record<string, boolean>
): MenuItemDef[] {
    const filterItems = (items: MenuItemDef[]): MenuItemDef[] => {
        return items
            .filter(item => {
                // Filter by tenant visibility
                if (item.mainTenantOnly && !isMainTenant) return false;

                // Filter by dynamic SSO app availability
                if (item.appId && (!availableApps || !availableApps[item.appId])) {
                    return false;
                }

                // Permission filtering for level 3 items (actual modules)
                if (item.level === 3 && permissionSlugs) {
                    const permission = permissionSlugs.get(item.slug);
                    // If no permission entry exists, hide the item
                    // If permission is NONE, hide the item
                    if (!permission || permission === 'NONE') return false;
                }

                return true;
            })
            .map(item => {
                if (!item.children) return item;

                const filteredChildren = filterItems(item.children);

                // If a group/section has no visible children after filtering, hide it too
                if (filteredChildren.length === 0 && item.level !== 3) return null;

                return { ...item, children: filteredChildren };
            })
            .filter((item): item is MenuItemDef => item !== null);
    };

    return filterItems(MENU_DEFINITION);
}

/**
 * Utility: Get all level 3 slugs from the menu definition.
 * Useful for Role Profile management to list all available modules.
 */
export function getAllModuleSlugs(): string[] {
    const slugs: string[] = [];
    const collect = (items: MenuItemDef[]) => {
        for (const item of items) {
            if (item.level === 3) slugs.push(item.slug);
            if (item.children) collect(item.children);
        }
    };
    collect(MENU_DEFINITION);
    return slugs;
}

export interface FlatMenuModule {
    slug: string;
    titleKey: string;
    icon: string;
    url: string;
    parentTitleKey: string;
    grandParentTitleKey: string;
    mainTenantOnly: boolean;
    external?: boolean;
}

/**
 * Stable numeric ID registry — slug → number.
 * Generated once at module load time, deterministic across hot-reloads
 * because MENU_DEFINITION order never changes.
 */
const _slugIdMap = new Map<string, number>();
let _idCounter = 1;

function _getStableId(slug: string): number {
    if (!_slugIdMap.has(slug)) _slugIdMap.set(slug, _idCounter++);
    return _slugIdMap.get(slug)!;
}

// Pre-populate IDs at module load so they are always consistent.
(function _prewarm(items: MenuItemDef[]) {
    for (const item of items) {
        _getStableId(item.slug);
        if (item.children) _prewarm(item.children);
    }
})(MENU_DEFINITION);

/** Shape returned by getMenuItemsFlat — compatible with legacy MenuItem type in roleProfiles / logProfiles. */
export interface MenuItemFlat {
    id: number;
    slug: string;
    /** i18n key — use t(titleKey) to get the translated label */
    titleKey: string;
    /** Translated label — populated by getMenuItemsFlat(t?) when a translator is provided */
    title: string;
    description: string;
    icon: string;
    url: string;
    level: 1 | 2 | 3;
    parentId: number | null;
    parentTitle: string;
    grandParentTitle: string;
    fullPath: string;
    isActive: boolean;
    mainTenantOnly: boolean;
}

/**
 * Utility: Get the full menu tree as a flat MenuItem-compatible list.
 *
 * Replaces the legacy /api/profile-menu/menu-items API endpoint.
 * Used by useRoleProfileData, useLogProfileData, and any other consumer
 * that needs ID-based parent-child relationships.
 *
 * @param t          - Optional i18n translator. When provided, `title` is
 *                     the translated label; otherwise the raw titleKey is used.
 * @param isMainTenant - When false, mainTenantOnly items are excluded.
 */
export function getMenuItemsFlat(
    t?: (key: string) => string,
    isMainTenant = true,
): MenuItemFlat[] {
    const result: MenuItemFlat[] = [];
    let index = 0;

    const translate = (key: string) => (t ? t(key) : key);

    for (const section of MENU_DEFINITION) {
        if (section.mainTenantOnly && !isMainTenant) continue;

        const sectionId = _getStableId(section.slug);
        const sectionTitle = translate(section.titleKey);

        result.push({
            id: sectionId,
            slug: section.slug,
            titleKey: section.titleKey,
            title: sectionTitle,
            description: section.slug,
            icon: section.icon,
            url: section.url,
            level: 1,
            parentId: null,
            parentTitle: '',
            grandParentTitle: '',
            fullPath: `1_${index++}_${sectionTitle}`,
            isActive: true,
            mainTenantOnly: !!section.mainTenantOnly,
        });

        if (!section.children) continue;

        for (const group of section.children) {
            if (group.mainTenantOnly && !isMainTenant) continue;

            const groupId = _getStableId(group.slug);
            const groupTitle = translate(group.titleKey);

            result.push({
                id: groupId,
                slug: group.slug,
                titleKey: group.titleKey,
                title: groupTitle,
                description: group.slug,
                icon: group.icon,
                url: group.url,
                level: 2,
                parentId: sectionId,
                parentTitle: sectionTitle,
                grandParentTitle: '',
                fullPath: `2_${index++}_${groupTitle}`,
                isActive: true,
                mainTenantOnly: !!group.mainTenantOnly,
            });

            if (!group.children) continue;

            for (const module of group.children) {
                if (module.mainTenantOnly && !isMainTenant) continue;

                const moduleId = _getStableId(module.slug);
                const moduleTitle = translate(module.titleKey);

                result.push({
                    id: moduleId,
                    slug: module.slug,
                    titleKey: module.titleKey,
                    title: moduleTitle,
                    description: module.slug,
                    icon: module.icon,
                    url: module.url,
                    level: 3,
                    parentId: groupId,
                    parentTitle: groupTitle,
                    grandParentTitle: sectionTitle,
                    fullPath: `3_${index++}_${moduleTitle}`,
                    isActive: true,
                    mainTenantOnly: !!module.mainTenantOnly,
                });
            }
        }
    }

    return result;
}

/**
 * Utility: Get all level-3 modules as a flat list with parent context.
 * Used by QuickActionsWidget to populate its config modal from the
 * hardcoded menu definition without any API calls.
 *
 * @param isMainTenant - When false, mainTenantOnly items are excluded.
 */
export function getFlatMenuModules(isMainTenant = true): FlatMenuModule[] {
    const result: FlatMenuModule[] = [];

    for (const section of MENU_DEFINITION) {
        if (section.mainTenantOnly && !isMainTenant) continue;
        if (!section.children) continue;

        for (const group of section.children) {
            if (group.mainTenantOnly && !isMainTenant) continue;

            // If the group has no children but HAS a URL (like apps_), it is effectively a module
            if (!group.children) {
                if (group.url) {
                    result.push({
                        slug: group.slug,
                        titleKey: group.titleKey,
                        icon: group.icon,
                        url: group.url,
                        parentTitleKey: section.titleKey,
                        grandParentTitleKey: section.titleKey,
                        mainTenantOnly: !!group.mainTenantOnly,
                        external: !!group.external
                    });
                }
                continue;
            }

            for (const module of group.children) {
                if (module.mainTenantOnly && !isMainTenant) continue;
                if (!module.url) continue;

                result.push({
                    slug: module.slug,
                    titleKey: module.titleKey,
                    icon: module.icon,
                    url: module.url,
                    parentTitleKey: group.titleKey,
                    grandParentTitleKey: section.titleKey,
                    mainTenantOnly: !!module.mainTenantOnly,
                    external: !!module.external
                });
            }
        }
    }

    return result;
}

/**
 * Utility: Find a menu item by its URL path.
 */
export function findMenuItemByPath(path: string, items: MenuItemDef[] = MENU_DEFINITION): MenuItemDef | null {
    for (const item of items) {
        if (item.url === path) return item;
        if (item.children) {
            const found = findMenuItemByPath(path, item.children);
            if (found) return found;
        }
    }
    return null;
}

/**
 * Utility: Find ancestor chain for a given path.
 * Returns [level1, level2, level3] items leading to the path.
 */
export function findAncestorChain(path: string, items: MenuItemDef[] = MENU_DEFINITION): MenuItemDef[] {
    const chain: MenuItemDef[] = [];

    const search = (items: MenuItemDef[], ancestors: MenuItemDef[]): boolean => {
        for (const item of items) {
            const currentChain = [...ancestors, item];
            if (item.url === path) {
                chain.push(...currentChain);
                return true;
            }
            if (item.children && search(item.children, currentChain)) {
                return true;
            }
        }
        return false;
    };

    // Try exact match first
    if (search(items, [])) return chain;

    // Fallback: match by prefix (for routes like /users/123)
    const searchPrefix = (items: MenuItemDef[], ancestors: MenuItemDef[]): boolean => {
        // Sort by URL length desc so we get the most specific match
        const sorted = [...items].sort((a, b) => (b.url?.length || 0) - (a.url?.length || 0));
        for (const item of sorted) {
            const currentChain = [...ancestors, item];
            if (item.url && path.startsWith(item.url) && item.url !== '/') {
                // Check if this item has children that match better
                if (item.children) {
                    if (searchPrefix(item.children, currentChain)) return true;
                }
                chain.push(...currentChain);
                return true;
            }
            if (item.children && searchPrefix(item.children, currentChain)) {
                return true;
            }
        }
        return false;
    };

    searchPrefix(items, []);
    return chain;
}
